import { useState, useEffect, useCallback } from 'react';
import { knowledgeApi } from '../lib/knowledge-api';
import { api } from '../lib/api-client';
import { useNotifications } from '../contexts/NotificationContext';

/**
 * Custom hook for managing knowledge data (collections and products).
 * Provides centralized data fetching and refresh logic for components
 * that need to interact with the knowledge system.
 *
 * Currently used by: Home.jsx
 * Note: Knowledge.jsx doesn't use this hook because it needs additional
 * data (categories) and uses a different products endpoint (filters API).
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.fetchOnMount - Whether to fetch data on mount (default: true)
 * @returns {Object} Knowledge data and refresh functions
 */
export function useKnowledgeData({ fetchOnMount = true } = {}) {
  const { fetchNotifications } = useNotifications();
  const [collections, setCollections] = useState([]);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch all collections from the API
   */
  const fetchCollections = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await knowledgeApi.collections.getAll();
      setCollections(data);
      return data;
    } catch (err) {
      console.error('Failed to fetch collections:', err);
      setError(err);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Fetch all products from the API
   */
  const fetchProducts = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await knowledgeApi.products.getAll();
      setProducts(data);
      return data;
    } catch (err) {
      console.error('Failed to fetch products:', err);
      setError(err);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Fetch both collections and products in parallel
   */
  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [collectionsData, productsData] = await Promise.all([
        knowledgeApi.collections.getAll(),
        knowledgeApi.products.getAll(),
      ]);
      setCollections(collectionsData);
      setProducts(productsData);
      return { collections: collectionsData, products: productsData };
    } catch (err) {
      console.error('Failed to fetch knowledge data:', err);
      setError(err);
      return { collections: [], products: [] };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch data on mount if requested
  useEffect(() => {
    if (fetchOnMount) {
      fetchAll();
    }
  }, [fetchOnMount, fetchAll]);

  // Trigger backend product check when products change
  useEffect(() => {
    if (!isLoading) {
      // Trigger backend to check and create notification if needed
      api.post('/api/v1/knowledge/products/health-check')
        .then(() => {
          // Refresh notifications to pick up any newly created notifications
          return fetchNotifications({ limit: 50 });
        })
        .catch(err => {
          console.error('[useKnowledgeData] Failed to trigger product health check:', err);
        });
    }
  }, [products.length, isLoading, fetchNotifications]);

  return {
    // Data
    collections,
    products,
    isLoading,
    error,

    // Refresh functions
    fetchCollections,
    fetchProducts,
    fetchAll,
  };
}
